import React, { useEffect, useState, useRef } from 'react';
import styles from './ReportModal.module.css';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// ★ 컴포넌트 Import
import WESInnerReportComponent from '../subComponets/WESInnerReportComponent';
import RNAInnerReportComponent from '../subComponets/RNAInnerReportComponent';
// ★ DEG 리포트 컴포넌트 추가 Import
import DEGInnerReportComponent from '../subComponets/DEGInnerReportComponent';

function ReportModal({ isOpen, onClose, sampleName, reportType = 'WES' }) {
    const { t } = useTranslation();

    const [reportData, setReportData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);

    const [pairedSamples, setPairedSamples] = useState([]);
    const [selectedPair, setSelectedPair] = useState('');
    const [isLoadingPairs, setIsLoadingPairs] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);

    const reportRef = useRef(null);
    const contentRef = useRef(null);

    // ★ DEG는 페어 샘플 조회가 필요 없을 수 있으므로 WES에서만 작동하도록 조건 추가도 가능하지만
    // 일단 기존 로직을 최대한 유지하면서 reportType이 WES일 때만 렌더링되게 처리됨.
    const isTumorSample = sampleName ? /TF|TT|TO/i.test(sampleName) : false;

    // 1. 모달 상태 관리 (Esc 종료 및 스타일)
    useEffect(() => {
        if (!isOpen) return;
        document.body.style.overflow = 'hidden';
        const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = 'auto';
            document.removeEventListener('keydown', handleKeyDown);
            // 초기화
            setReportData(null);
            setError(null);
            setPairedSamples([]);
            setSelectedPair('');
            setZoomLevel(1);
        };
    }, [isOpen, onClose]);

    // 2. [통합 로직] 페어 샘플 탐색 후 리포트 데이터 가져오기
    useEffect(() => {
        if (!isOpen || !sampleName) return;

        const fetchDataFlow = async () => {
            setIsLoading(true);
            setError(null);
            
            try {
                let token = localStorage.getItem('userInfo');
                if (token && token.includes('"')) token = JSON.parse(token);
                const headers = { 'Authorization': `Bearer ${token}` };

                let currentPair = '';

                // A. Tumor 샘플인 경우 페어 샘플 먼저 조회 (WES 대상)
                if (isTumorSample && reportType === 'WES') {
                    setIsLoadingPairs(true);
                    try {
                        const pairRes = await axios.get(`/api/reports/pairs/${sampleName}`, { headers });
                        const pairs = pairRes.data.pairedSamples || [];
                        setPairedSamples(pairs);
                        if (pairs.length > 0) {
                            currentPair = pairs[0];
                            setSelectedPair(currentPair);
                        }
                    } catch (err) {
                        console.error("Pair fetch error:", err);
                    } finally {
                        setIsLoadingPairs(false);
                    }
                }

                // B. 리포트 데이터 조회 (위에서 확정된 currentPair 사용)
                let response;
                if (reportType === 'RNA') {
                    response = await axios.get(`/api/reports/RNA/${sampleName}`, { headers });
                } else if (reportType === 'DEG') {
                    // ★ DEG 데이터 요청 엔드포인트 (추후 백엔드 개발 시 활성화/수정)
                    // 현재는 에러 방지를 위해 가짜 데이터(빈 객체)를 세팅하거나, 
                    // 백엔드가 없으면 에러가 날 것이므로 try-catch에서 잡히게 둡니다.
                    // 임시로 하드코딩된 빈 객체를 넣고 싶다면: response = { data: { sampleName } };
                    response = await axios.get(`/api/reports/DEG/${sampleName}`, { headers });
                } else {
                    // WES 처리
                    const pairQuery = currentPair ? `?paired=${currentPair}` : '';
                    response = await axios.get(`/api/reports/NGS/${sampleName}${pairQuery}`, { headers });
                }

                setReportData(response.data);
            } catch (err) {
                console.error("Report fetch error:", err);
                setError(t('리포트를 불러오는 중 오류가 발생했습니다.'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchDataFlow();
    }, [isOpen, sampleName, reportType, isTumorSample]); // selectedPair는 의존성에서 제거하여 중복 호출 방지

    // 3. 셀렉트 박스로 사용자가 페어를 직접 변경했을 때만 실행되는 별도 Effect (WES 전용)
    const isFirstRender = useRef(true);
    useEffect(() => {
        // 첫 렌더링(모달 열릴 때) 시 실행 방지
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        
        // 사용자가 수동으로 페어를 바꿨을 때만 데이터 다시 가져옴 (WES만 해당)
        if (isOpen && selectedPair && !isLoading && reportType === 'WES') {
            const fetchUpdatedReport = async () => {
                setIsLoading(true);
                try {
                    let token = localStorage.getItem('userInfo');
                    if (token && token.includes('"')) token = JSON.parse(token);
                    const response = await axios.get(`/api/reports/NGS/${sampleName}?paired=${selectedPair}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    setReportData(response.data);
                } catch (err) {
                    console.error(err);
                } finally {
                    setIsLoading(false);
                }
            };
            fetchUpdatedReport();
        }
    }, [selectedPair]);

    // 4. 줌 로직
    useEffect(() => {
        const handleNativeWheel = (e) => {
            if (e.ctrlKey) {
                const contentEl = contentRef.current;
                if (contentEl && contentEl.contains(e.target)) {
                    e.preventDefault();
                    if (e.deltaY < 0) setZoomLevel(p => Math.min(p + 0.1, 2.0));
                    else setZoomLevel(p => Math.max(p - 0.1, 0.5));
                }
            }
        };
        window.addEventListener('wheel', handleNativeWheel, { passive: false });
        return () => window.removeEventListener('wheel', handleNativeWheel);
    }, []);

    // 5. PDF 다운로드 로직
    const handleDownloadPdf = async () => {
        if (!reportRef.current) return;
        try {
            setIsDownloading(true);
            const currentZoom = zoomLevel;
            setZoomLevel(1);
            // Base64 이미지가 10장 이상이므로, 브라우저가 화면을 리페인트하고 
            // 모든 이미지를 렌더링할 수 있도록 대기 시간을 800ms ~ 1000ms 정도로 늘려줍니다.
            await new Promise(resolve => setTimeout(resolve, 800)); 
        
            const pages = reportRef.current.querySelectorAll('.pdf-page');
            if (pages.length === 0) { setZoomLevel(currentZoom); return; }

            const pdf = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            for (let i = 0; i < pages.length; i++) {
                // 1. scale을 4로 증가 (필요에 따라 3으로 타협 가능)
                const canvas = await html2canvas(pages[i], { 
                    scale: 4, 
                    useCORS: true, 
                    logging: false, 
                    backgroundColor: '#ffffff' 
                });
                
                // 2. 화질 저하가 없는 PNG 포맷으로 변경
                const imgData = canvas.toDataURL('image/png');
                const imgProps = pdf.getImageProperties(imgData);
                
                let printWidth = pdfWidth;
                let printHeight = (imgProps.height * pdfWidth) / imgProps.width;
                
                if (printHeight > pdfHeight) {
                    printWidth = (imgProps.width * pdfHeight) / imgProps.height;
                    printHeight = pdfHeight;
                }
                
                if (i > 0) pdf.addPage();
                
                // 3. addImage 옵션에서 'JPEG'를 'PNG'로 변경하고, 'FAST' 옵션 제거 (고화질 유지)
                pdf.addImage(imgData, 'PNG', 0, 0, printWidth, printHeight);
            }

            const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            const fileNamePair = (reportType === 'WES' && selectedPair) ? `_vs_${selectedPair}` : '';
            pdf.save(`Report_${reportType}_${sampleName}${fileNamePair}_${dateStr}.pdf`);
            setZoomLevel(currentZoom);
        } catch (err) {
            console.error(err);
            alert(t('PDF 생성 중 오류가 발생했습니다.'));
            setZoomLevel(1);
        } finally {
            setIsDownloading(false);
        }
    };

    // ★ reportType에 따라 렌더링할 컴포넌트 결정
    let InnerReportComponent;
    if (reportType === 'RNA') {
        InnerReportComponent = RNAInnerReportComponent;
    } else if (reportType === 'DEG') {
        InnerReportComponent = DEGInnerReportComponent;
    } else {
        InnerReportComponent = WESInnerReportComponent;
    }

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div className={styles.headerTitleWrap}>
                        <h2 className={styles.modalTitle}>{t('리포트 상세')} - {sampleName} ({reportType})</h2>
                        {reportType === 'WES' && isTumorSample && (
                            <select className={styles.pairSelect} value={selectedPair} onChange={(e) => setSelectedPair(e.target.value)} disabled={isLoadingPairs || isLoading}>
                                {isLoadingPairs ? <option value="">{t('페어 탐색 중...')}</option>
                                    : pairedSamples.length > 0 ? pairedSamples.map(p => <option key={p} value={p}>{p}</option>)
                                        : <option value="">{t('매칭된 페어 없음')}</option>}
                            </select>
                        )}
                    </div>
                    <button className={styles.closeIcon} onClick={onClose}>&times;</button>
                </div>

                <div className={styles.modalContent} ref={contentRef} title={t("Ctrl 키를 누른 채 마우스 휠을 돌리면 줌이 조절됩니다.")}>
                    {isLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
                            <p>{t('리포트를 불러오는 중입니다...')}</p>
                        </div>
                    ) : error ? (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '50px', width: '100%' }}>
                            <p style={{ color: 'red' }}>{error}</p>
                        </div>
                    ) : (
                        <div className={styles.a4ViewerContainer}>
                            <div className={styles.a4Viewer} ref={reportRef} style={{
                                transform: `scale(${zoomLevel})`,
                                transformOrigin: 'top center',
                                transition: 'transform 0.1s ease-out',
                                marginBottom: `${(zoomLevel - 1) * 100}%`
                            }}>
                                <InnerReportComponent data={reportData} />
                            </div>
                        </div>
                    )}
                </div>

                <div className={styles.modalFooter}>
                    <div className={styles.zoomControls}>
                        <button onClick={() => setZoomLevel(p => Math.max(p - 0.1, 0.5))} className={styles.zoomBtn}>-</button>
                        <span className={styles.zoomText}>{Math.round(zoomLevel * 100)}%</span>
                        <button onClick={() => setZoomLevel(p => Math.min(p + 0.1, 2.0))} className={styles.zoomBtn}>+</button>
                        <button onClick={() => setZoomLevel(1)} className={styles.zoomResetBtn}>{t('초기화')}</button>
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button className={styles.closeBtn} onClick={onClose} disabled={isDownloading}>{t('닫기')}</button>
                        {/* ★ 데이터가 없더라도 일단 다운로드 버튼은 보이게 할지 여부는 기존 로직 유지 */}
                        <button className={styles.primaryBtn} onClick={handleDownloadPdf} disabled={isLoading || !!error || isDownloading || !reportData}>
                            {isDownloading ? t('PDF 생성 중...') : t('PDF 다운로드')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ReportModal;